/**
 * Billing Hook
 * Fetches project status and subscriptions for billing display.
 * Data is cached globally via TanStack Query and accessible from anywhere.
 *
 * @see BILLING_IMPLEMENTATION_GUIDE.md
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { useApiStore } from "@finitless/shared/stores";
import { useBillingStore } from "../stores/billingStore";
import { useIsProjectOwner } from "./useIsProjectOwner";

export { useIsProjectOwner } from "./useIsProjectOwner";
import { useAuthStatus } from "@finitless/shared/hooks";
import { appConfig } from "@finitless/shared/config";

/** Query key factory for billing - enables cache invalidation across the app */
export const billingQueryKeys = {
  all: ["billing"],
  project: () => [...billingQueryKeys.all, "project"],
  subscriptions: () => [...billingQueryKeys.all, "subscriptions"],
  plans: () => [...billingQueryKeys.all, "plans"],
  cards: () => [...billingQueryKeys.all, "cards"],
  cardRequirements: () => [...billingQueryKeys.all, "cardRequirements"],
  billingAuth: () => [...billingQueryKeys.all, "billingAuth"],
};

/**
 * Direct billing API request helper.
 * Payment methods endpoints live on the billing API (api-billing.ordering.co),
 * not the dashboard API. This helper calls them directly like the accounts repo does.
 */
const billingApiRequest = async (path, options = {}) => {
  const { token, method, body } = options;
  const response = await fetch(`${appConfig.billing_api_url}${path}`, {
    method: method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (data?.error) {
    const msg =
      typeof data.result === "string"
        ? data.result
        : Array.isArray(data.result)
          ? data.result[0]
          : "Billing API error";
    throw new Error(msg);
  }
  return data.result;
};

/**
 * Calculate days until a date (for trial/suspension countdown).
 * Uses Math.trunc (truncate toward zero) to match moment.diff behavior —
 * e.g. 5 days 12 hours ago returns -5, not -6.
 *
 * @param {string} isoDate - ISO date string (e.g. "2025-04-15T00:00:00.000Z")
 * @returns {number|null} Days until the date (negative if past), or null if invalid
 */
export const getCurrentDiffDays = (isoDate) => {
  if (!isoDate) return null;
  const current = new Date();
  const compared = new Date(isoDate);
  const diffMs = compared - current;
  return Math.trunc(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Hook to fetch project status (GET /current)
 * Returns current_status, current_status_until for trial/past_due display
 */
const useProjectCurrent = (enabled) => {
  const project = useApiStore((state) => state.project);

  return useQuery({
    queryKey: [...billingQueryKeys.project(), project],
    queryFn: async () => {
      const response = await apiClient.get("/current");
      if (response?.error) {
        throw new Error(response.result || "Failed to fetch project status");
      }
      return response?.result ?? null;
    },
    enabled: enabled && !!project,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Get the numeric project ID from the /current query cache.
 * The billing API requires the numeric ID (e.g., 17574), not the project slug.
 */
const useBillingProjectId = () => {
  const project = useApiStore((state) => state.project);
  const { data } = useQuery({
    queryKey: [...billingQueryKeys.project(), project],
    queryFn: async () => {
      const response = await apiClient.get("/current");
      if (response?.error) {
        throw new Error(response.result || "Failed to fetch project status");
      }
      return response?.result ?? null;
    },
    enabled: !!project,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  return data?.id ?? null;
};

/**
 * Get billing auth credentials (token + billing user ID).
 * The billing API uses a separate auth system from the dashboard.
 * 1. Fetches an SSO token via the dashboard API
 * 2. Uses that token to call GET /users/me on the billing API
 * 3. Returns { billingToken, billingUserId } for payment method calls
 */
const useBillingAuth = (enabled = true) => {
  const { isAuthenticated } = useAuthStatus();

  return useQuery({
    queryKey: billingQueryKeys.billingAuth(),
    queryFn: async () => {
      // Step 1: Get billing token from dashboard API
      const tokenResponse = await apiClient.get(
        "/integrations/internal/tokens/current/",
      );
      const billingToken = tokenResponse?.result?.access_token;
      if (!billingToken) {
        throw new Error("No billing token received");
      }

      // Step 2: Get billing user via billing API
      const userResponse = await fetch(
        `${appConfig.billing_api_url}/users/me`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${billingToken}`,
          },
        },
      );
      const userData = await userResponse.json();
      if (userData?.error || !userData?.result?.id) {
        throw new Error("Failed to get billing user");
      }

      return {
        billingToken,
        billingUserId: userData.result.id,
      };
    },
    enabled: enabled && isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Hook to fetch subscriptions (GET /billing/subscriptions)
 * Returns plan info including upgrade_plan_id
 */
const useBillingSubscriptions = (enabled) => {
  const project = useApiStore((state) => state.project);

  return useQuery({
    queryKey: [...billingQueryKeys.subscriptions(), project],
    queryFn: async () => {
      const response = await apiClient.get("/billing/subscriptions");
      if (response?.error) {
        throw new Error(response.result || "Failed to fetch subscriptions");
      }
      return response?.result ?? [];
    },
    enabled: enabled && !!project,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Combined billing hook - fetches project status and subscriptions.
 * Returns normalized billing state for use across the app.
 *
 * @returns {Object} Billing state
 * @returns {Object} project - Project from GET /current (id, code, name, current_status, current_status_until)
 * @returns {Object} plan - First subscription's plan (id, name, upgrade_plan_id, prices, etc.)
 * @returns {Array} subscriptions - Raw subscriptions array
 * @returns {string} currentStatus - Shorthand: active | trialing | past_due | incomplete | suspended | canceled
 * @returns {string|null} currentStatusUntil - ISO date for trial end / suspension
 * @returns {number|null} trialDaysRemaining - Days until current_status_until (when trialing)
 * @returns {boolean} isLoading
 * @returns {boolean} isError
 * @returns {Error|null} error
 * @returns {Function} refetch
 */
export const useBilling = () => {
  const { isAuthenticated } = useAuthStatus();
  const projectQuery = useProjectCurrent(isAuthenticated);
  const subscriptionsQuery = useBillingSubscriptions(isAuthenticated);

  const project = projectQuery.data ?? null;
  const subscriptions = subscriptionsQuery.data ?? [];
  const isLoading = projectQuery.isLoading || subscriptionsQuery.isLoading;
  const isError = projectQuery.isError || subscriptionsQuery.isError;
  const error = projectQuery.error || subscriptionsQuery.error;

  // Derive plan from first active/trialing subscription
  const activeSubscription = Array.isArray(subscriptions)
    ? subscriptions.find(
        (s) =>
          s?.status === "active" ||
          s?.status === "trialing" ||
          s?.status === "past_due",
      )
    : null;
  const plan = activeSubscription?.plan?.plan ?? null;

  // Flatten features from all products into { [feature.key]: boolean }
  const features = useMemo(() => {
    const products = activeSubscription?.plan?.products;
    if (!products) return {};
    const map = {};
    for (const product of products) {
      for (const feat of product.features || []) {
        const key = feat?.feature?.key;
        if (key) {
          map[key] = !!(feat.enabled && feat.feature?.enabled);
        }
      }
    }
    return map;
  }, [activeSubscription]);

  const currentStatus = project?.current_status ?? null;
  const currentStatusUntil =
    project?.current_status_until ??
    project?.project_current_status_until ??
    null;
  const trialDaysRemaining =
    currentStatusUntil && currentStatus === "trialing"
      ? getCurrentDiffDays(currentStatusUntil)
      : null;

  const refetch = async () => {
    await Promise.all([projectQuery.refetch(), subscriptionsQuery.refetch()]);
  };

  return {
    project,
    plan,
    features,
    subscriptions,
    currentStatus,
    currentStatusUntil,
    trialDaysRemaining,
    upgradePlanId: plan?.upgrade_plan_id ?? null,
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Hook to redirect user to billing portal with SSO token.
 * Fetches token from GET /integrations/internal/tokens/current/ and redirects.
 *
 * @returns {Object} { redirectToBillingPortal, isRedirecting }
 */
export const useBillingRedirect = () => {
  const redirectMutation = useMutation({
    mutationKey: ["billing", "redirect"],
    mutationFn: async () => {
      const response = await apiClient.get(
        "/integrations/internal/tokens/current/",
      );
      if (response?.error) {
        throw new Error(response.result || "Failed to get billing access");
      }
      const { access_token, referer_url } = response?.result ?? {};
      if (!access_token) {
        throw new Error("No access token received");
      }
      const billingUrl =
        appConfig?.billing_url ||
        (referer_url
          ? `https://${referer_url.replace(/^https?:\/\//, "")}`
          : "https://accounts.finitless.com");
      const url = `${billingUrl.replace(/\/$/, "")}?token=${encodeURIComponent(access_token)}`;
      window.location.assign(url);
    },
    retry: false,
  });

  return {
    redirectToBillingPortal: redirectMutation.mutateAsync,
    isRedirecting: redirectMutation.isPending,
  };
};

/**
 * Hook to fetch public plans for upgrade (GET /billing/plans/public)
 */
export const useBillingPlans = (enabled = true) => {
  const project = useApiStore((state) => state.project);
  const { isAuthenticated } = useAuthStatus();

  return useQuery({
    queryKey: [...billingQueryKeys.plans(), project],
    queryFn: async () => {
      const response = await apiClient.get("/billing/plans/public");
      if (response?.error) {
        throw new Error(response.result || "Failed to fetch plans");
      }
      return response?.result ?? [];
    },
    enabled: enabled && isAuthenticated && !!project,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Mutation to create subscription (POST /billing/subscriptions/plans)
 * Request: { plan_id, period: 'monthly'|'annual', replace: true }
 * Response may include: client_secret (Stripe), redirect_url, or success
 */
export const useCreateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["billing", "create-subscription"],
    mutationFn: async ({ plan_id, period }) => {
      const response = await apiClient.post("/billing/subscriptions/plans", {
        plan_id,
        period,
        replace: true,
      });
      if (response?.error) {
        throw new Error(response.result || "Failed to create subscription");
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.all });
    },
    retry: false,
  });
};

/**
 * Hook to fetch saved billing cards from the billing API directly.
 * GET {billingApiUrl}/users/{userId}/paymethods/cards?project_id={project}
 * Returns array of cards: { id, data: { brand, last4 }, default }
 */
export const useBillingCards = (enabled = true) => {
  const projectId = useBillingProjectId();
  const { data: billingAuth } = useBillingAuth(enabled);

  return useQuery({
    queryKey: [
      ...billingQueryKeys.cards(),
      projectId,
      billingAuth?.billingUserId,
    ],
    queryFn: async () => {
      return billingApiRequest(
        `/users/${billingAuth.billingUserId}/paymethods/cards?project_id=${projectId}`,
        { token: billingAuth.billingToken },
      );
    },
    enabled: enabled && !!projectId && !!billingAuth,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Hook to fetch Stripe SetupIntent requirements for adding a new card.
 * GET {billingApiUrl}/users/{userId}/paymethods/requirements?type=add_card&project_id={project}
 * Returns { publishable_key, client_secret, secret }
 */
export const useAddCardRequirements = (enabled = false) => {
  const projectId = useBillingProjectId();
  const { data: billingAuth } = useBillingAuth(enabled);

  return useQuery({
    queryKey: [
      ...billingQueryKeys.cardRequirements(),
      projectId,
      billingAuth?.billingUserId,
    ],
    queryFn: async () => {
      return billingApiRequest(
        `/users/${billingAuth.billingUserId}/paymethods/requirements?type=add_card&project_id=${projectId}`,
        { token: billingAuth.billingToken },
      );
    },
    enabled: enabled && !!projectId && !!billingAuth,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Mutation to save a new card via the billing API.
 * POST {billingApiUrl}/users/{userId}/paymethods/cards?project_id={project}
 * Request: { source_id: 'pm_xxx', default: true }
 */
export const useSaveNewCard = () => {
  const queryClient = useQueryClient();
  const projectId = useBillingProjectId();
  const { data: billingAuth } = useBillingAuth();

  return useMutation({
    mutationKey: ["billing", "save-card"],
    mutationFn: async ({ source_id }) => {
      return billingApiRequest(
        `/users/${billingAuth.billingUserId}/paymethods/cards?project_id=${projectId}`,
        {
          token: billingAuth.billingToken,
          method: "POST",
          body: { source_id, default: true },
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.cards() });
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys.cardRequirements(),
      });
    },
    retry: false,
  });
};

/**
 * Mutation to set a card as default via the billing API.
 * POST {billingApiUrl}/users/{userId}/paymethods/cards/{cardId}/default?project_id={project}
 */
export const useSetDefaultCard = () => {
  const queryClient = useQueryClient();
  const projectId = useBillingProjectId();
  const { data: billingAuth } = useBillingAuth();

  return useMutation({
    mutationKey: ["billing", "set-default-card"],
    mutationFn: async ({ cardId }) => {
      return billingApiRequest(
        `/users/${billingAuth.billingUserId}/paymethods/cards/${cardId}/default?project_id=${projectId}`,
        {
          token: billingAuth.billingToken,
          method: "POST",
          body: {
            user_id: billingAuth.billingUserId,
            user_paymethod_id: cardId,
          },
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.cards() });
    },
    retry: false,
  });
};

/**
 * Hook to determine if the Past Due banner should be visible.
 * Used by AppLayout for padding and by PastDueBanner for rendering.
 * Banner is hidden when upgrade/payment modal is open.
 *
 * @returns {boolean} True when banner should show (past_due + project owner + not loading + modal closed)
 */
export const usePastDueBannerVisible = () => {
  const isProjectOwner = useIsProjectOwner();
  const { currentStatus, isLoading } = useBilling();
  const showUpgradeModal = useBillingStore((state) => state.showUpgradeModal);

  return (
    !!isProjectOwner &&
    currentStatus === "past_due" &&
    !isLoading &&
    !showUpgradeModal
  );
};
